import React from 'react';
import { CheckCircle, Loader2, Search, User } from 'lucide-react';
import { Customer } from '@/types';

interface CustomerSearchPanelProps {
  customerSearch: string;
  onCustomerSearchChange: (value: string) => void;
  filteredCustomers: Customer[];
  isCustomerSearchLoading: boolean;
  customerSearchError: string;
  selectedCustomerId: string | null;
  selectedCustomerName: string;
  selectedCustomerEmail: string;
  onSelectExistingCustomer: (customer: Customer) => void;
  onCreateNewCustomer: () => void;
}

export const CustomerSearchPanel: React.FC<CustomerSearchPanelProps> = ({
  customerSearch,
  onCustomerSearchChange,
  filteredCustomers,
  isCustomerSearchLoading,
  customerSearchError,
  selectedCustomerId,
  selectedCustomerName,
  selectedCustomerEmail,
  onSelectExistingCustomer,
  onCreateNewCustomer
}) => (
  <>
    <label className="block text-sm font-bold text-slate-700 mb-2 flex items-center gap-2">
      <User className="w-4 h-4" /> Patient Selection
    </label>
    {!selectedCustomerId ? (
      <div className="relative mb-4">
        <Search className="absolute left-3 top-3 text-slate-400 w-5 h-5" />
        <input
          type="text"
          className="w-full pl-10 pr-4 py-3 border rounded-xl outline-none focus:ring-2 focus:ring-teal-500 bg-slate-50 text-sm"
          placeholder="Search existing patient by name or email..."
          value={customerSearch}
          onChange={(e) => onCustomerSearchChange(e.target.value)}
        />
        {customerSearch && (
          <div className="absolute top-full left-0 w-full bg-white border rounded-xl shadow-xl mt-1 z-20 overflow-hidden divide-y">
            {filteredCustomers.map((c) => (
              <button
                key={c.id}
                onClick={() => onSelectExistingCustomer(c)}
                className="w-full p-3 text-left hover:bg-teal-50 flex justify-between items-center transition-colors"
              >
                <div>
                  <div className="font-bold text-sm text-slate-800">{c.name}</div>
                  <div className="text-xs text-slate-500">{c.email}</div>
                </div>
                <CheckCircle className="w-4 h-4 text-teal-500 opacity-0 group-hover:opacity-100" />
              </button>
            ))}
            {isCustomerSearchLoading && (
              <div className="p-3 text-center text-xs text-slate-400 flex items-center justify-center gap-2">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Searching patients...
              </div>
            )}
            {customerSearchError && (
              <div className="p-3 text-center text-xs text-red-600">{customerSearchError}</div>
            )}
            {!isCustomerSearchLoading && !customerSearchError && filteredCustomers.length === 0 && (
              <div className="p-4 text-center text-sm text-slate-400">No matches found.</div>
            )}
          </div>
        )}
      </div>
    ) : (
      <div className="bg-teal-50 border border-teal-100 p-4 rounded-xl flex justify-between items-center mb-4">
        <div className="flex items-center gap-3">
          <div className="bg-teal-100 p-2 rounded-full text-teal-600">
            <User className="w-5 h-5" />
          </div>
          <div>
            <div className="font-bold text-slate-800">{selectedCustomerName}</div>
            <div className="text-xs text-slate-500">{selectedCustomerEmail}</div>
          </div>
        </div>
        <button
          onClick={onCreateNewCustomer}
          className="text-xs font-bold text-teal-600 hover:underline"
        >
          Change
        </button>
      </div>
    )}
  </>
);
